import { format } from "date-fns";
import numeral from "numeral";
import React from "react";
import { Badge } from "react-bootstrap";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { Link } from "react-router-dom";

// icons
import Icon from "@mdi/react";
import { mdiFile, mdiOpenInNew } from "@mdi/js";

const Grouped = ({
  yearValue,
  projects,
  handleViewProject,
  page,
  perPage,
  showColumn,
  sortKey,
}) => {
  const progressStatusOptions = useSelector(
    (state) => state.utilsReducer.dropdownItems.progressStatusOptions,
  );

  const data = projects;
  let cumulativeIndex = 0;

  return (
    <React.Fragment>
      {data.map((program) => {
        // JUMLAH must add up exactly what the rows below display: the
        // selected year's Peruntukan Disemak, its lejar and its baki — for the
        // projects that survived the filters.
        const yearRows = program.main_projects
          .flatMap((item) => item.projects)
          .map((project) =>
            project?.s3p_data?.find(
              (item) => item.year.toString() === yearValue.toString(),
            ),
          )
          .filter(Boolean);

        const totalAllocation = yearRows.reduce(
          (acc, item) => acc + parseFloat(item.revised_total_allocation || 0),
          0,
        );

        const totalCurrentBalance = yearRows.reduce(
          (acc, item) => acc + parseFloat(item.current_balance || 0),
          0,
        );

        const totalExpenditure = yearRows
          .flatMap((item) => item.overall_financial_data_by_months || [])
          .reduce((acc, obj) => acc + parseFloat(obj.lejar || 0), 0);

        return (
          <React.Fragment key={program.id}>
            <tr>
              <td />
              <td>
                <div className='d-flex'>
                  <div
                    className='bg-info px-2 text-white rounded-2 fw-bold w-100 text-center text-truncate'
                    style={{ fontSize: "12px" }}
                  >
                    {program?.prefix_type}
                    {program?.ministry_id} {program?.code}
                  </div>
                </div>
              </td>
              <td colSpan={10}>{program?.name}</td>
            </tr>
            {program?.main_projects.map((mainProject) => {
              return (
                <React.Fragment key={mainProject.id}>
                  <tr>
                    <td />
                    <td>
                      <div className='d-flex'>
                        <div
                          className='bg-secondary px-2 text-white rounded-2 fw-bold w-100 text-center fw-bold'
                          style={{ fontSize: "12px" }}
                        >
                          {mainProject?.code}
                        </div>
                      </div>
                    </td>
                    <td colSpan={10}>{mainProject?.name}</td>
                  </tr>
                  {mainProject?.projects.map((project, idx) => {
                    cumulativeIndex++;

                    const sentReport = project?.reports?.filter(
                      (item) => item.is_sent === true,
                    );
                    const isDraft =
                      project?.reports?.some(
                        (item) => item.is_sent === false,
                      ) || null;

                    const overallPercentage = sentReport
                      ? sentReport[0]?.progress
                      : 0;

                    const findYear = project?.s3p_data.find(
                      (item) => item.year.toString() === yearValue.toString(),
                    );

                    const lejar =
                      findYear?.overall_financial_data_by_months?.reduce(
                        (acc, item) => (acc += parseFloat(item.lejar)),
                        0,
                      );

                    const latestReport = sentReport ? sentReport[0] : null;

                    // No sent report — fall back to the project's stored status
                    const progressStatus =
                      latestReport?.current_progress_status ||
                      progressStatusOptions?.find(
                        (item) => item.code === project.progress_status,
                      );

                    return (
                      <React.Fragment key={project.id}>
                        <tr
                          style={{ cursor: "pointer" }}
                          onClick={() => handleViewProject(project.id)}
                        >
                          <td className='text-center'>{cumulativeIndex}</td>
                          <td className='text-center fw-bolder'>
                            <div>{project?.code}</div>
                            <div>
                              {isDraft && (
                                <span className='ms-2'>
                                  <Tooltip
                                    placement='right'
                                    title='Laporan ini mempunyai draf'
                                    arrow
                                  >
                                    <Icon path={mdiFile} size='16px' />
                                  </Tooltip>
                                </span>
                              )}
                            </div>
                          </td>
                          <td>{project?.name}</td>
                          <td className='text-center'>
                            {project?.ministry?.short_form}
                          </td>
                          <td className='text-center'>
                            {project?.executing_agency?.name}
                          </td>
                          <td className='text-center'>
                            {latestReport?.month && latestReport?.year
                              ? `${latestReport.month}/${latestReport.year}`
                              : "-"}
                          </td>
                          <td className='text-center'>
                            <Tooltip
                              title={progressStatus?.description}
                              placement='top'
                              arrow
                            >
                              <Badge
                                bg=''
                                className={`text-uppercase w-100`}
                                style={{
                                  backgroundColor: progressStatus?.color,
                                }}
                              >
                                {progressStatus?.label}
                              </Badge>
                            </Tooltip>
                          </td>
                          <td className='text-center fw-bolder'>
                            {overallPercentage || 0} %
                          </td>
                          <td className='text-end text-truncate'>
                            {numeral(findYear?.revised_total_allocation).format(
                              "0,0.00",
                            )}
                          </td>
                          <td className='text-end text-truncate'>
                            {numeral(lejar).format("0,0.00")}
                          </td>
                          <td className='text-end text-truncate'>
                            {numeral(findYear?.current_balance).format(
                              "0,0.00",
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation(e)}>
                            <div className='d-flex justify-content-center'>
                              <Link
                                to={`/laporan/kemaskini-kemajuan-projek/${project.id}`}
                                target='_blank'
                                rel='noopener noreferrer'
                              >
                                <Icon path={mdiOpenInNew} size='18px' />
                              </Link>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })}
            <tr style={{ backgroundColor: "#eeeeee" }}>
              <td colSpan={8} className='text-end fw-bold'>
                JUMLAH
              </td>
              <td className='text-end fw-bold'>
                {numeral(totalAllocation).format("0,0.00")}
              </td>
              <td className='text-end fw-bold'>
                {numeral(totalExpenditure).format("0,0.00")}
              </td>
              <td className='text-end fw-bold'>
                {numeral(totalCurrentBalance).format("0,0.00")}
              </td>
              <td />
            </tr>
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
};

export default Grouped;
